import { useEffect, useMemo } from "react";
import { ProgressSpinner } from "primereact/progressspinner";

// Clients
import { getAuthClient, getLoginClient } from "sj-ui-toolkit/src/client";

// Hooks
import { useLoginFlow } from "sj-ui-toolkit/src/hooks";

// Helpers
import { IS_LOCAL_MODE } from "sj-ui-toolkit/src/constants/env";
import { LoginAppRoutes } from "sj-ui-toolkit/src/constants/routes";

// Handles incoming redirects from SAML identity providers, exchanges the code for an access token, and completes the login flow
export default function SamlRedirectPage() {
  const authClient = getAuthClient();
  const loginClient = getLoginClient();
  const { handlePostLogin } = useLoginFlow();

  const searchParams = new URLSearchParams(window.location.search);
  const code = searchParams.get("code") || "";
  const sjAdmin = searchParams.get("sj_admin") === "true";

  const stateOptions = useMemo(() => {
    const state = new URLSearchParams();

    if (IS_LOCAL_MODE) {
      state.set("local", "true");
    }
    if (sjAdmin) {
      state.set("sj_admin", "true");
    }

    return state.size > 0 ? state.toString() : undefined;
  }, [sjAdmin]);

  useEffect(() => {
    async function handleAuthentication() {
      if (!code) {
        console.error("No cognito code provided in the URL");
        return;
      }

      try {
        const { error } = await loginClient.authenticateWithCode({
          code,
          redirect_url:
            window.location.origin +
            (sjAdmin
              ? `${LoginAppRoutes.routes.samlRedirect.route}?sj_admin=true`
              : LoginAppRoutes.routes.samlRedirect.route),
          state: stateOptions,
        });

        if (error) {
          throw new Error(error.message || "Authentication failed");
        }

        const { data, error: ex_error } = await authClient.exchangeForAccessToken({
          scopes: { sjAdmin },
        });
        if (ex_error) {
          throw new Error(ex_error.message || "Failed to exchange code for access token");
        }
        if (!data?.access_token) throw new Error("No access token returned");
        await handlePostLogin(data.access_token, sjAdmin);
      } catch (error) {
        console.error("Error during authentication:", error);
      }
    }

    handleAuthentication();
  }, []);

  return (
    <div className="h-screen flex items-center justify-center" role="status" aria-live="polite">
      <ProgressSpinner />
      <span className="sr-only">Redirecting, please wait</span>
    </div>
  );
}
